/**
 * PR-V5 — hatch-pattern screen-area gating.
 *
 * # Why this exists
 *
 * At extreme zoom-out (685k-entity П35Р fixture, zoom-extents), most
 * HATCH entities cover only a handful of CSS pixels. Sampling a 64×64
 * pattern texture and tiling it across the mesh's UVs produces a
 * coloured smear at sub-tile area — the user can not perceive the
 * pattern, only the average colour. The pattern texture allocation,
 * UV channel emission, and `RepeatWrapping` GPU sampling are pure
 * cost.
 *
 * The gate classifies a hatch by world-space area × camera zoom² (=
 * screen-space area in pixels²) and tells the caller whether to fall
 * back to a flat solid-coloured mesh:
 *
 *   * `screenAreaPx² < 16`  (i.e. < 4×4 px)  → solid fallback
 *   * otherwise                                → full pattern texture
 *
 * AutoCAD itself ramps to a flat-colour fill at extreme zoom-out (the
 * pattern visibly disappears below the unit-step). The threshold is
 * conservative — at 4×4 px even SOLID hatches pixel-snap to a single
 * dot, so anything below that emits a smear regardless.
 *
 * # CLAUDE.md ПРИОРИТЕТ #1 — full fidelity at visible zoom
 *
 * As soon as the hatch grows past 4×4 px on screen, the full pattern
 * path runs unmodified — every family stroke, dash, angle, scale,
 * custom DXF group code 53/43/45/46 lands intact. This is an
 * emit-time optimization, not a fidelity reduction.
 *
 * # This gate is PATTERN-only — gradient hatches are NEVER gated
 *
 * A gradient hatch (DXF 450=1) is NOT screen-gated. AutoCAD's "tiny
 * hatch → solid" path is the HPMAXLINES pattern line-count safeguard,
 * which is structurally inapplicable to a gradient: a gradient-filled
 * hatch is a SOLID-class fill with ZERO pattern lines (ezdxf: "a
 * gradient filled hatch is also a solid filled hatch"), so there is no
 * line count to cap. No CAD collapses a gradient ramp to one colour by
 * screen size; the only gradient flattening is the global FILLMODE
 * outline toggle (out of scope here). So `buildHatchFillMesh` ALWAYS
 * renders the full axial/radial ramp for a gradient regardless of
 * screen area, and {@link shouldUseSolidFallback} is consulted ONLY for
 * non-solid PATTERN hatches (CLAUDE.md ПРИОРИТЕТ #1 — no
 * representative-colour substitution). The only flat fallback for a
 * gradient is the degenerate-bbox NaN guard inside `buildGradientMaterial`,
 * which is zoom-independent.
 *
 * # Bbox-area approximation
 *
 * Computing the precise polygon area would require walking every loop
 * vertex (shoelace) — that scales O(N) on the loop count. The bbox
 * area is `(maxX-minX) × (maxY-minY)`, an *overestimate* for any
 * non-rectangular hatch. Overestimates are safe for the gate: we
 * would rather pay full pattern cost on a borderline-perceptible
 * hatch than skip a barely-visible one.
 */

/**
 * Pixel² thresholds delimiting hatch screen-area buckets. Exposed as a
 * `const` so callers (and unit tests) can introspect them without
 * re-deriving the numbers.
 */
export const HATCH_SCREEN_AREA_THRESHOLDS_PX2 = {
  /** Below this — fall back to a solid-coloured mesh (no pattern texture). */
  solidFallback: 16,
} as const;

/**
 * Decide whether a hatch entity should fall back to a solid-coloured
 * mesh in lieu of the full pattern texture.
 *
 * Returns `true` when `worldArea × cameraZoom² < 16` (= < 4×4 px on
 * screen). Negative / zero / non-finite inputs are treated as
 * effectively-zero area → fallback is `true`.
 */
export function shouldUseSolidFallback(
  hatchAreaWorld: number,
  cameraZoom: number,
): boolean {
  if (!Number.isFinite(hatchAreaWorld) || hatchAreaWorld <= 0) return true;
  if (!Number.isFinite(cameraZoom) || cameraZoom <= 0) return true;
  const screenAreaPx2 = hatchAreaWorld * cameraZoom * cameraZoom;
  return screenAreaPx2 < HATCH_SCREEN_AREA_THRESHOLDS_PX2.solidFallback;
}

/**
 * Compute an upper-bound estimate of the hatch's world-space area
 * from its bounds rectangle. The bbox area is an *overestimate* of
 * every non-rectangular hatch — preferred over the exact shoelace
 * walk because we'd rather render the pattern than skip a
 * borderline-perceptible hatch (CLAUDE.md PRIORITET #1).
 */
export function bboxAreaWorld(bounds: {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}): number {
  const w = Math.max(0, bounds.maxX - bounds.minX);
  const h = Math.max(0, bounds.maxY - bounds.minY);
  return w * h;
}
